import { useContext, useState, useEffect, useMemo, forwardRef } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { createPortal } from 'react-dom'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { t } from 'ttag'

import { Button } from '@h2/components/buttons/button'
import { Text } from '@h2/components/typography/text'
import { IWantToIcon } from '@h2/components/icons/iWantTo'
import { HomeIcon } from '@h2/components/icons/home'
import { SearchIcon } from '@h2/components/icons/search'
import { VideoGreenIcon } from '@h2/components/icons/videoGreen'
import { EnvelopeIcon } from '@h2/components/icons/envelope'
import { InfoCircleIcon } from '@h2/components/icons/infoCircle'
import { CareerHubIcon } from '@h2/components/icons/careerHub'
import { ContinueCircleIcon } from '@h2/components/icons/continueCircle'
import { CogIcon } from '@h2/components/icons/cog'
import { LoginIcon } from '@h2/components/icons/login'
import { PB90T, PB97T, PG, BLACK, WHITE, SLB } from '@h2/components/colors'
import { features } from '@h2/components/media'
import { openSigninDialog } from '@main/actions/signinOrRegisterDialog'
import { Heading } from '@h2/components/typography/heading'
import { museoFont } from '@h2/components/mixins'
import { Globals } from '@h2/globals'

import { Search } from './search'
import { IWantTo } from './iWantTo'
import { Notification } from './notification'
import { ActiveLessons } from './activeLessons'
import { ManagementMenu } from './managementMenu'
import { EducatorCode } from './educatorCode'
import { navList, navLink } from './mixins'

import { Context } from '../context'
import { Profile } from '../profile'
import { Management } from '../management'
import { Link as LinkComponent } from '../../link'
import { BodyOverflowHidden } from '../../bodyOverflowHidden'
import { VIEW_MODE_MANAGEMENT } from '../../../constants'

const NavigationPadding = createGlobalStyle`
  #nocontent {
    padding-bottom: ${({ navHeight }) => navHeight ?? '52px'};
  }
`
const MuseoHeading = styled(Heading)`
  ${museoFont}
  font-weight: 900;

  > span {
    font-weight: 500;
  }
`
const Link = styled(LinkComponent)`
  ${navLink};
  position: relative;
`
const ButtonRelative = styled(Button)`
  position: relative;
`
const getBorderColor = ({ isEarlyLearnerMode }) =>
  isEarlyLearnerMode ? 'border-color: transparent;' : null
const Nav = styled.nav`
  > ul {
    ${navList};
    background-color: transparent;

    > li {
      width: ${({ isNavianceElementary }) => (isNavianceElementary ? 33.3 : 20)}%;

      &:last-child {
        display: none;
      }

      > button {
        &.active {
          background-color: ${PG};

          > span:first-child > span {
            color: white;
            font-weight: 700;
          }
        }
      }

      &#login {
        width: ${({ isNavianceElementary }) => (isNavianceElementary ? 60 : 40)}%;

        button {
          background-color: ${PB90T};
        }
      }
    }
  }

  @media (${features.minWidth.laptop}) {
    > ul {
      justify-content: flex-end;

      > li {
        width: 80px;
        flex-shrink: 0;
        border-right: unset;
        border-color: ${PB90T};

        &:first-child {
          border-left: 1px solid ${PB90T};
        }

        &:last-child {
          width: auto;
          border-right: 1px solid ${PB90T};
          display: list-item;
        }

        &#active-lessons {
          position: relative;
          flex: 1 1 370px;
          width: auto;
          max-width: 425px;
        }

        &#login {
          width: 140px;
        }

        &#profile,
        &#educator-code {
          width: auto;
        }

        &#profile {
          ${getBorderColor}
          > button {
            background-color: ${PB90T};

            > span:first-child > span:first-child {
              display: flex;

              svg {
                position: relative;
                top: 2px;
                margin-left: 8px;
              }
            }

            &.active {
              > span:first-child span {
                color: black;
              }
            }
          }

          &:hover {
            > button {
              background-color: ${PB97T};
            }
          }
        }
      }
    }
  }
`
const EarlyLearnerLi = styled.li`
  border-color: transparent !important;
  padding: 9px;
  width: fit-content !important;
  margin: ${({ margin }) => margin};
`
const EarlyLearnerHeaderAligned = styled(EarlyLearnerLi)`
  margin: 0 auto 0 0;
  padding: 9px 0;
`
const PillLink = styled(Link)`
  display: none;

  @media (${features.minWidth.desktop}) {
    :link,
    :active,
    :visited {
      display: flex;
      background-color: ${SLB};
      outline: ${WHITE} solid 3px;
      text-decoration: none;
      color: ${WHITE};
      border-radius: 25px;
      margin: 0 20px 0 0;
      font-size: 20px;
      :hover {
        background-color: ${WHITE};
        color: ${BLACK};
      }
    }
  }
`
const Dock = styled.div`
  position: fixed;
  bottom: 0;
  width: 100%;
  z-index: 1000;
  background-color: white;

  @media (${features.minWidth.laptop}) {
    position: unset;
    bottom: unset;
    z-index: 0;
    background-color: transparent;
  }
`
const defaultActivation = {
  search: false,
  lessons: false,
  management: false,
  iWantTo: false
}
const Navigation = forwardRef(function Navigation(_props, ref) {
  const history = useHistory()
  const dispatch = useDispatch()
  const {
    isLoggedIn,
    isMobile,
    requireAuthentication,
    userProfile,
    userMenu,
    viewMode,
    navHeight,
    isNavianceElementary,
    isNavigator,
  } = useContext(Context)
  const { isEarlyLearnerMode } = useContext(Globals)
  const [active, setActive] = useState(() => ({ ...defaultActivation }))
  const [isNavActive] = useMemo(() => {
    const keys = Object.keys(active)
    let isActive = false
    let key = null
    let i = 0

    do {
      if (active[keys[i++]]) {
        key = keys[i - 1]
        isActive = true
      }
    } while (!key && i < keys.length)

    return [isActive, key]
  }, [active])
  const lessons = useMemo(() => {
    if (userMenu?.activePlan) {
      return [userMenu.activePlan].concat(userMenu.additionalPlans)
    }

    return []
  }, [userMenu])
  const activePlan = userMenu?.activePlan
  const activePlanId = activePlan?.id
  const helpAttributes = { href: '/help/redirect', ssr: true, target: '_blank' }
  const render = () => (
    <>
      <Dock ref={ref}>
        <Nav
          isNavianceElementary={isNavianceElementary}
          isEarlyLearnerMode={isEarlyLearnerMode}>
          <ul>
            {isEarlyLearnerMode && (
              <EarlyLearnerHeaderAligned>
                <MuseoHeading level="2" removeMargins={true} color={WHITE}>
                  {t`Early Learner Mode`}
                </MuseoHeading>
              </EarlyLearnerHeaderAligned>
            )}
            {isEarlyLearnerMode && activePlan && (
              <EarlyLearnerLi>
                <PillLink
                  href={
                    activePlanId
                      ? `/planning/assignments/detail/${activePlanId}`
                      : `/planning/goal`
                  }
                  isHoverStyled={false}
                  icon={ContinueCircleIcon}>
                  {activePlan?.isStarted ? t`Continue Lesson` : t`Start New Lesson`}
                </PillLink>
              </EarlyLearnerLi>
            )}
            {isEarlyLearnerMode && (
              <EarlyLearnerLi>
                <PillLink href={`/`} isHoverStyled={false} icon={HomeIcon} align="right">
                  {t`Home`}
                </PillLink>
              </EarlyLearnerLi>
            )}

            {!isEarlyLearnerMode && viewMode === VIEW_MODE_MANAGEMENT && !isMobile && (
              <li id="educator-code">
                <EducatorCode />
              </li>
            )}
            {!isEarlyLearnerMode && isLoggedIn && isMobile && (
              <li>
                {lessons.length ? (
                  <>
                    <ButtonRelative
                      iconAtTop
                      variant="outlined"
                      icon={VideoGreenIcon}
                      className={active.lessons ? 'active' : null}
                      size="medium"
                      onClick={() => {
                        setActive(state => ({
                          ...defaultActivation,
                          lessons: !state.lessons
                        }))
                      }}>
                      <Text color={BLACK} as="span" size="small">{t`Your Lessons`}</Text>
                      <Notification count={lessons.length} />
                    </ButtonRelative>
                    {active.lessons && <ActiveLessons lessons={lessons} />}
                  </>
                ) : (
                  <Link href="/planning/assignments">
                    <VideoGreenIcon />
                    <Text color={BLACK} as="span" size="small">{t`Your Lessons`}</Text>
                  </Link>
                )}
              </li>
            )}
            {!isEarlyLearnerMode && isLoggedIn && !isMobile && lessons.length > 0 && (
              <li id="active-lessons">
                <ActiveLessons lessons={lessons} />
              </li>
            )}
            {!isEarlyLearnerMode && !(isLoggedIn && isMobile) && (
              <li>
                <Link href={requireAuthentication && !userProfile ? '/signin' : '/'}>
                  <HomeIcon />
                  <Text color={BLACK} as="span" size="small">{t`Home`}</Text>
                </Link>
              </li>
            )}
            {!isEarlyLearnerMode && isLoggedIn && (
              <li>
                <Link href="/messaging/">
                  <EnvelopeIcon />
                  <Text color={BLACK} as="span" size="small">{t`Messages`}</Text>
                  {userMenu?.notifications?.messageCount > 0 && (
                    <Notification count={userMenu.notifications.messageCount} />
                  )}
                </Link>
              </li>
            )}
            {!isEarlyLearnerMode && !isNavianceElementary && isMobile && (
              <li>
                <Button
                  iconAtTop
                  variant="outlined"
                  color="#f4c223"
                  icon={SearchIcon}
                  size="medium"
                  className={active.search ? 'active' : null}
                  onClick={() => {
                    setActive(state => ({
                      ...defaultActivation,
                      search: !state.search
                    }))
                  }}>
                  <Text color={BLACK} as="span" size="small">{t`Search`}</Text>
                </Button>
                {active.search && (
                  <Search
                    onClickClose={() => {
                      setActive({ ...defaultActivation })
                    }}
                  />
                )}
              </li>
            )}
            {!isEarlyLearnerMode && isLoggedIn && (
              <>
                {viewMode !== VIEW_MODE_MANAGEMENT ? (
                  <li>
                    <Link href="/careerhub">
                      <CareerHubIcon />
                      <Text color={BLACK} as="span" size="small">{t`CareerHub`}</Text>
                    </Link>
                  </li>
                ) : isMobile ? (
                  <li>
                    <Button
                      iconAtTop
                      variant="outlined"
                      icon={CogIcon}
                      size="medium"
                      className={active.management ? 'active' : null}
                      onClick={() => {
                        setActive(state => ({
                          ...defaultActivation,
                          management: !state.management
                        }))
                      }}>
                      <Text color={BLACK} as="span" size="small">{t`Management`}</Text>
                    </Button>
                    {active.management && <ManagementMenu />}
                  </li>
                ) : (
                  <li>
                    <Management />
                  </li>
                )}
              </>
            )}
            {!isEarlyLearnerMode && !isNavianceElementary && (
              <li>
                <Button
                  iconAtTop
                  variant="outlined"
                  icon={IWantToIcon}
                  size="medium"
                  aria-expanded={active.iWantTo}
                  className={active.iWantTo ? 'active' : null}
                  onClick={() => {
                    setActive(state => ({
                      ...defaultActivation,
                      iWantTo: !state.iWantTo
                    }))
                  }}>
                  <Text color={BLACK} as="span" size="small">{t`I want to...`}</Text>
                </Button>
                {!(isMobile && !active.iWantTo) && (
                  <IWantTo
                    isActive={active.iWantTo}
                    onClose={() => {
                      setActive({
                        ...defaultActivation,
                        iWantTo: false
                      })
                    }}
                  />
                )}
              </li>
            )}

            {isLoggedIn ? (
              !isMobile && (
                <li id="profile">
                  <Profile isEarlyLearnerMode={isEarlyLearnerMode} />
                </li>
              )
            ) : (
              <li id="login">
                <Button
                  isFullHeight
                  variant="outlined"
                  align="center-right"
                  size="medium"
                  icon={LoginIcon}
                  onClick={() => dispatch(openSigninDialog('signIn'))}>
                  <Text color={BLACK} as="span" weight="bold">{t`Sign In`}</Text>
                </Button>
              </li>
            )}
            {(!isEarlyLearnerMode && isLoggedIn && (isNavigator || !userProfile.isSubscriber) && !userProfile.isBelow13 ) && (
              <li>
                <Link {...helpAttributes}>
                  <InfoCircleIcon />
                  <Text color={BLACK} as="span" size="small">{t`Help`}</Text>
                </Link>
              </li>
            )}
          </ul>
        </Nav>
      </Dock>
      {isMobile && <NavigationPadding navHeight={navHeight} />}
      {isNavActive && <BodyOverflowHidden />}
    </>
  )

  useEffect(() => {
    const unlisten = history.listen(() => {
      setActive(defaultActivation)
    })

    return () => unlisten()
  }, [history])

  if (isMobile) {
    return createPortal(render(), document.body)
  }

  return render()
})

export { Navigation }
